import React, { useState, useEffect } from "react";
import PageLayout, { SectionReveal } from "../components/PageLayout";
import PageHero from "../components/PageHero";
import PageBanner from "../components/PageBanner";
import { Check, X } from "lucide-react";
import { useLang, useT } from "../context/LanguageContext";

// Varsayılan kur (API yanıt vermezse kullanılır)
const DEFAULT_USD_TRY_RATE = 47.55;

// Fiyat formatlama: USD baz fiyatı seçili para birimine çevirir
const formatPrice = (priceUSD, currency, rate) => {
  if (currency === "TRY") {
    const tl = priceUSD * rate;
    if (tl < 1) return `₺${tl.toFixed(3).replace(".", ",")}`;
    return `₺${tl.toFixed(1).replace(".", ",")}`;
  }
  // USD
  if (priceUSD < 0.01) return `$${priceUSD.toFixed(4)}`;
  if (priceUSD < 1) return `$${priceUSD.toFixed(3)}`;
  return `$${priceUSD.toFixed(2)}`;
};

/* ─────────────────────────────────────────────
   Plan Cards
   ───────────────────────────────────────────── */
const PlanCards = () => {
  const t = useT();
  const { lang } = useLang();
  const isTr = lang === "tr";

  const paygoFeatures = [
    t("pricing.plan.paygo.feature1"),
    t("pricing.plan.paygo.feature2"),
    t("pricing.plan.paygo.feature3"),
    t("pricing.plan.paygo.feature4"),
    t("pricing.plan.paygo.feature5"),
    t("pricing.plan.paygo.feature6"),
  ];

  const customFeatures = [
    t("pricing.plan.custom.feature1"),
    t("pricing.plan.custom.feature2"),
    t("pricing.plan.custom.feature3"),
    t("pricing.plan.custom.feature4"),
    t("pricing.plan.custom.feature5"),
    t("pricing.plan.custom.feature6"),
    t("pricing.plan.custom.feature7"),
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
      {/* PAYGO Card */}
      <div
        className="relative p-8 rounded-2xl"
        style={{
          background: "var(--card-bg)",
          color: "var(--fg)",
          border: "1px solid var(--border)",
        }}
      >
        <span
          className="inline-block px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider mb-4"
          style={{ background: "var(--fg)", color: "var(--bg)" }}
        >
          {t("pricing.plan.paygo.badge")}
        </span>
        <h3 className="text-[22px] font-bold mb-1">{t("pricing.plan.paygo.name")}</h3>
        <p className="text-[13px] mb-6" style={{ color: "var(--muted)" }}>
          {t("pricing.plan.paygo.description")}
        </p>
        <div className="flex items-baseline gap-1 mb-6">
          <span className="text-[28px] font-bold tracking-tight" style={{ color: "var(--fg)" }}>
            {isTr ? "Kullanım Fiyatı" : "Usage Cost"}
          </span>
        </div>
        <a
          href={isTr ? "https://app.verasist.ai" : "https://app.verasist.ai"}
          className="w-full py-3 rounded-full font-semibold text-[14px] transition-all duration-200 mb-7 inline-block text-center"
          style={{ background: "var(--fg)", color: "var(--bg)" }}
        >
          {t("pricing.plan.paygo.cta")}
        </a>
        <p
          className="text-[12px] font-semibold uppercase tracking-wider mb-3"
          style={{ color: "var(--muted)" }}
        >
          {t("pricing.featuresTitle")}
        </p>
        <div className="space-y-3">
          {paygoFeatures.map((feat) => (
            <div key={feat} className="flex items-start gap-2.5">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "rgba(16,185,129,0.12)" }}>
                <Check size={12} style={{ color: "#10b981" }} />
              </span>
              <span className="text-[13.5px]" style={{ color: "var(--fg)" }}>{feat}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Enterprise Card */}
      <div
        className="relative p-8 rounded-2xl"
        style={{
          background: "var(--card-bg)",
          color: "var(--fg)",
          border: "2px solid var(--brand-cyan)",
        }}
      >
        <span
          className="inline-block px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider mb-4"
          style={{ background: "var(--brand-navy)", color: "#ffffff" }}
        >
          {t("pricing.plan.custom.badge")}
        </span>
        <h3 className="text-[22px] font-bold mb-1">{t("pricing.plan.custom.name")}</h3>
        <p className="text-[13px] mb-6" style={{ color: "var(--muted)" }}>
          {t("pricing.plan.custom.description")}
        </p>
        <div className="flex items-baseline gap-1 mb-6">
          <span className="text-[28px] font-bold tracking-tight" style={{ color: "var(--fg)" }}>
            {isTr ? "Özel Fiyat" : "Custom"}
          </span>
        </div>
        <a
          href={
            isTr
              ? `mailto:hello@verasist.ai?subject=${encodeURIComponent("Kurumsal Fiyatlandırma Görüşmesi Talebi")}&body=${encodeURIComponent("Merhaba Verasist Ekibi,\n\nKurumsal çözümleriniz hakkında bilgi almak ve özel fiyatlandırma için bir görüşme planlamak istiyorum.\n\nŞirket:\nTelefon:\nUygun olduğum gün/saat:\n\nTeşekkürler,")}`
              : `mailto:hello@verasist.ai?subject=${encodeURIComponent("Enterprise Pricing Discussion Request")}&body=${encodeURIComponent("Hello Verasist Team,\n\nI would like to learn more about your enterprise solutions and schedule a call to discuss custom pricing.\n\nCompany:\nPhone:\nPreferred day/time:\n\nThanks,")}`
          }
          className="w-full py-3 rounded-full font-semibold text-[14px] transition-all duration-200 mb-7 inline-block text-center"
          style={{ background: "var(--brand-navy)", color: "#ffffff" }}
        >
          {t("pricing.plan.custom.cta")}
        </a>
        <p
          className="text-[12px] font-semibold uppercase tracking-wider mb-3"
          style={{ color: "var(--muted)" }}
        >
          {t("pricing.featuresTitle")}
        </p>
        <div className="space-y-3">
          {customFeatures.map((feat) => (
            <div key={feat} className="flex items-start gap-2.5">
              <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ background: "rgba(75,111,212,0.15)" }}>
                <Check size={12} style={{ color: "var(--brand-cyan)" }} />
              </span>
              <span className="text-[13.5px]" style={{ color: "var(--fg)" }}>{feat}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Feature Comparison Table
   ───────────────────────────────────────────── */
const CompareTable = ({ currency, usdTryRate }) => {
  const t = useT();
  const { lang } = useLang();
  const isTr = lang === "tr";
  const rate = usdTryRate || DEFAULT_USD_TRY_RATE;

  // USD baz fiyatlar (dakika başına)
  const PRICE_LLM = 0.0021;
  const PRICE_STT = 0.00557;
  const PRICE_TTS = 0.0606;
  const PRICE_PLATFORM = 0.0105;
  const PRICE_PHONE = 0.0105;
  const PRICE_CONCURRENCY = 10;   // $10 per line
  const PRICE_ZERO_RETENTION = 1000; // $1,000/mo

  const concurrencyPrice = currency === "TRY"
    ? `₺${Math.round(PRICE_CONCURRENCY * rate).toLocaleString("tr-TR")}`
    : `$${PRICE_CONCURRENCY}`;

  const zeroRetentionPrice = currency === "TRY"
    ? `₺${Math.round(PRICE_ZERO_RETENTION * rate).toLocaleString("tr-TR")}/ay`
    : `$${PRICE_ZERO_RETENTION.toLocaleString()}/mo`;

  const rows = [
    {
      category: t("pricing.page.compare.usage"),
      items: [
        { label: t("pricing.page.compare.concurrency"), build: `10 ${t("pricing.page.compare.minIncluded")} + ${concurrencyPrice} ${t("pricing.page.compare.perLine")}`, scale: t("pricing.page.compare.custom") },
      ],
    },
    {
      category: isTr ? "Platform ve Altyapı" : "Platform & Infrastructure",
      sub: isTr ? "Dakika başına tahmini maliyetler" : "Estimated per-minute costs",
      items: [
        { label: isTr ? "LLM (Dil Modeli)" : "LLM (Language Model)", build: formatPrice(PRICE_LLM, currency, rate), scale: t("pricing.page.compare.volumeBased") },
        { label: isTr ? "STT (Konuşma-Metin)" : "STT (Speech-to-Text)", build: formatPrice(PRICE_STT, currency, rate), scale: t("pricing.page.compare.volumeBased") },
        { label: isTr ? "TTS (Metin-Ses)" : "TTS (Text-to-Speech)", build: formatPrice(PRICE_TTS, currency, rate), scale: t("pricing.page.compare.volumeBased") },
        { label: isTr ? "Verasist Platform" : "Verasist Platform", build: formatPrice(PRICE_PLATFORM, currency, rate), scale: t("pricing.page.compare.volumeBased") },
        { label: isTr ? "Telefon Hattı / Numara" : "Phone Line / Number", build: formatPrice(PRICE_PHONE, currency, rate), scale: t("pricing.page.compare.volumeBased") },
      ],
    },
    {
      category: t("pricing.page.compare.channels"),
      items: [
        { label: isTr ? "Telefon" : "Phone", build: t("pricing.page.compare.check"), scale: t("pricing.page.compare.check") },
        { label: "WhatsApp", build: t("pricing.page.compare.check"), scale: t("pricing.page.compare.check") },
        { label: isTr ? "Web (WebRTC)" : "Web (WebRTC)", build: t("pricing.page.compare.check"), scale: t("pricing.page.compare.check") },
      ],
    },
    {
      category: t("pricing.page.compare.dataRetention"),
      items: [
        { label: isTr ? "Çalıştırma kayıtları" : "Run recordings", build: t("pricing.page.compare.days14"), scale: t("pricing.page.compare.daysCustom") },
        { label: isTr ? "Transkriptler" : "Transcripts", build: t("pricing.page.compare.days14"), scale: t("pricing.page.compare.daysCustom") },
      ],
    },
    {
      category: t("pricing.page.compare.security"),
      items: [
        { label: isTr ? "KVKK / GDPR" : "GDPR / KVKK", build: t("pricing.page.compare.check"), scale: t("pricing.page.compare.check") },
        { label: "SOC 2", build: t("pricing.page.compare.noExtra"), scale: t("pricing.page.compare.check") },
        { label: "SSO / RBAC", build: t("pricing.page.compare.noExtra"), scale: t("pricing.page.compare.check") },
        { label: isTr ? "Sıfır Veri Saklama" : "Zero Data Retention", build: zeroRetentionPrice, scale: zeroRetentionPrice },
      ],
    },
    {
      category: t("pricing.page.compare.reliability"),
      items: [
        { label: isTr ? "Uptime" : "Uptime", build: "99.9%", scale: t("pricing.page.compare.uptime") },
        { label: isTr ? "Destek" : "Support", build: t("pricing.page.compare.community"), scale: t("pricing.page.compare.supportSLA") },
        { label: isTr ? "Hesap yönetimi" : "Account management", build: t("pricing.page.compare.noExtra"), scale: t("pricing.page.compare.dedicatedTeam") },
      ],
    },
  ];

  const renderVal = (val) => {
    if (val === t("pricing.page.compare.check")) {
      return <span className="inline-flex items-center justify-center w-5 h-5 rounded-full" style={{ background: "rgba(16,185,129,0.12)" }}><Check size={12} style={{ color: "#10b981" }} /></span>;
    }
    if (val === t("pricing.page.compare.noExtra")) {
      return <span style={{ color: "var(--muted)", opacity: 0.4 }}>—</span>;
    }
    return val;
  };

  return (
    <div className="max-w-4xl mx-auto overflow-x-auto">
      <table className="w-full text-left border-collapse" style={{ minWidth: "600px" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--border)" }}>
            <th className="py-3 pr-4 text-[13px] font-semibold" style={{ color: "var(--fg)" }}></th>
            <th className="py-3 px-4 text-[13px] font-bold" style={{ color: "var(--fg)" }}>
              {t("pricing.page.compare.build")}
            </th>
            <th className="py-3 pl-4 text-[13px] font-bold" style={{ color: "var(--brand-cyan)" }}>
              {t("pricing.page.compare.scale")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((section, si) => (
            <React.Fragment key={section.category}>
              <tr>
                <td
                  colSpan={3}
                  className="pt-6 pb-2 text-[11px] font-bold uppercase tracking-wider"
                  style={{ color: "var(--muted)" }}
                >
                  {section.category}
                </td>
              </tr>
              {section.sub && (
                <tr>
                  <td
                    colSpan={3}
                    className="pb-2 text-[12px]"
                    style={{ color: "var(--muted)", opacity: 0.7 }}
                  >
                    {section.sub}
                  </td>
                </tr>
              )}
              {section.items.map((item, ii) => (
                <tr
                  key={ii}
                  style={{ borderBottom: "1px solid var(--border)" }}
                >
                  <td className="py-3 pr-4 text-[13px]" style={{ color: "var(--fg)" }}>
                    {typeof item.label === "function" ? item.label() : item.label}
                  </td>
                  <td className="py-3 px-4 text-[13px]" style={{ color: "var(--fg)" }}>
                    {renderVal(item.build)}
                  </td>
                  <td className="py-3 pl-4 text-[13px] font-medium" style={{ color: "var(--brand-cyan)" }}>
                    {renderVal(item.scale)}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Cost Calculator
   ───────────────────────────────────────────── */
const CostCalculator = ({ currency, usdTryRate }) => {
  const t = useT();
  const { lang } = useLang();
  const isTr = lang === "tr";
  const [callsPerMonth, setCallsPerMonth] = useState(1000);
  const [callLength, setCallLength] = useState(2);
  const rate = usdTryRate || DEFAULT_USD_TRY_RATE;

  // Birim maliyetler (dakika başına USD)
  const llmCostPerMin = 0.0021;
  const sttCostPerMin = 0.00557;
  const ttsCostPerMin = 0.0606;
  const platformCostPerMin = 0.0105;
  const phoneLineCostPerMin = 0.0105;

  const totalMinutes = callsPerMonth * callLength;
  const llmTotalUSD = totalMinutes * llmCostPerMin;
  const sttTotalUSD = totalMinutes * sttCostPerMin;
  const ttsTotalUSD = totalMinutes * ttsCostPerMin;
  const platformTotalUSD = totalMinutes * platformCostPerMin;
  const phoneLineTotalUSD = totalMinutes * phoneLineCostPerMin;
  const grandTotalUSD = llmTotalUSD + sttTotalUSD + ttsTotalUSD + platformTotalUSD + phoneLineTotalUSD;

  // USD → seçili para birimine dönüştür
  const convertAmount = (usdAmount) => {
    if (currency === "TRY") return usdAmount * rate;
    return usdAmount;
  };

  const formatCalc = (usdVal) => {
    const converted = convertAmount(usdVal);
    if (currency === "TRY") {
      return `₺${converted.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
    }
    if (converted < 0.01) return `$${converted.toFixed(4)}`;
    if (converted < 1) return `$${converted.toFixed(2)}`;
    return `$${converted.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatUnitPrice = (usdPrice) => {
    if (currency === "TRY") {
      const tl = usdPrice * rate;
      if (tl < 1) return `₺${tl.toFixed(3).replace(".", ",")}`;
      return `₺${tl.toFixed(1).replace(".", ",")}`;
    }
    if (usdPrice < 0.01) return `$${usdPrice.toFixed(4)}`;
    return `$${usdPrice.toFixed(3)}`;
  };

  return (
    <div className="max-w-2xl mx-auto">
      {/* Sliders */}
      <div className="space-y-6 mb-8">
        <div>
          <div className="flex justify-between mb-2">
            <label className="text-[13px] font-medium" style={{ color: "var(--fg)" }}>
              {isTr ? "Aylık Çağrı Sayısı" : t("pricing.page.calculator.callsPerMonth")}
            </label>
            <span className="text-[13px] font-bold" style={{ color: "var(--brand-cyan)" }}>
              {callsPerMonth.toLocaleString()}
            </span>
          </div>
          <input
            type="range"
            min="100"
            max="100000"
            step="100"
            value={callsPerMonth}
            onChange={(e) => setCallsPerMonth(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, var(--brand-cyan) 0%, var(--brand-cyan) ${((callsPerMonth - 100) / (100000 - 100)) * 100}%, var(--border) ${((callsPerMonth - 100) / (100000 - 100)) * 100}%, var(--border) 100%)`,
              accentColor: "var(--brand-cyan)",
            }}
          />
          <div className="flex justify-between mt-1">
            <span className="text-[11px]" style={{ color: "var(--muted)" }}>100</span>
            <span className="text-[11px]" style={{ color: "var(--muted)" }}>100,000</span>
          </div>
        </div>

        <div>
          <div className="flex justify-between mb-2">
            <label className="text-[13px] font-medium" style={{ color: "var(--fg)" }}>
              {isTr ? "Ortalama Çağrı Süresi" : t("pricing.page.calculator.callLength")}
            </label>
            <span className="text-[13px] font-bold" style={{ color: "var(--brand-cyan)" }}>
              {callLength} {isTr ? "dk" : "min"}
            </span>
          </div>
          <input
            type="range"
            min="1"
            max="10"
            step="0.5"
            value={callLength}
            onChange={(e) => setCallLength(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, var(--brand-cyan) 0%, var(--brand-cyan) ${((callLength - 1) / (10 - 1)) * 100}%, var(--border) ${((callLength - 1) / (10 - 1)) * 100}%, var(--border) 100%)`,
              accentColor: "var(--brand-cyan)",
            }}
          />
          <div className="flex justify-between mt-1">
            <span className="text-[11px]" style={{ color: "var(--muted)" }}>1</span>
            <span className="text-[11px]" style={{ color: "var(--muted)" }}>10</span>
          </div>
        </div>
      </div>

      {/* Cost Breakdown */}
      <div
        className="rounded-2xl p-6 mb-4"
        style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}
      >
        <div className="space-y-3">
          <div className="flex justify-between text-[13px]">
            <span style={{ color: "var(--muted)" }}>
              {isTr ? "LLM (Dil Modeli)" : "LLM (Language Model)"} ({formatUnitPrice(llmCostPerMin)}{t("pricing.page.calculator.perMin")})
            </span>
            <span style={{ color: "var(--fg)" }}>{formatCalc(llmTotalUSD)}</span>
          </div>
          <div className="flex justify-between text-[13px]">
            <span style={{ color: "var(--muted)" }}>
              {isTr ? "STT (Konuşma-Metin)" : "STT (Speech-to-Text)"} ({formatUnitPrice(sttCostPerMin)}{t("pricing.page.calculator.perMin")})
            </span>
            <span style={{ color: "var(--fg)" }}>{formatCalc(sttTotalUSD)}</span>
          </div>
          <div className="flex justify-between text-[13px]">
            <span style={{ color: "var(--muted)" }}>
              {isTr ? "TTS (Metin-Ses)" : "TTS (Text-to-Speech)"} ({formatUnitPrice(ttsCostPerMin)}{t("pricing.page.calculator.perMin")})
            </span>
            <span style={{ color: "var(--fg)" }}>{formatCalc(ttsTotalUSD)}</span>
          </div>
          <div className="flex justify-between text-[13px]">
            <span style={{ color: "var(--muted)" }}>
              {isTr ? "Verasist Platform" : "Verasist Platform"} ({formatUnitPrice(platformCostPerMin)}{t("pricing.page.calculator.perMin")})
            </span>
            <span style={{ color: "var(--fg)" }}>{formatCalc(platformTotalUSD)}</span>
          </div>
          <div className="flex justify-between text-[13px]">
            <span style={{ color: "var(--muted)" }}>
              {isTr ? "Telefon Hattı / Numara" : "Phone Line / Number"} ({formatUnitPrice(phoneLineCostPerMin)}{t("pricing.page.calculator.perMin")})
            </span>
            <span style={{ color: "var(--fg)" }}>{formatCalc(phoneLineTotalUSD)}</span>
          </div>
          <div
            className="flex justify-between text-[15px] font-bold pt-3"
            style={{ borderTop: "1px solid var(--border)" }}
          >
            <span style={{ color: "var(--fg)" }}>{isTr ? "Toplam Aylık Maliyet" : t("pricing.page.calculator.totalMonthly")}</span>
            <span style={{ color: "var(--brand-cyan)" }}>{formatCalc(grandTotalUSD)}</span>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-center" style={{ color: "var(--muted)", opacity: 0.6 }}>
        {isTr
          ? "* Tahmini maliyettir. Gerçek maliyet seçilen model sağlayıcılara ve kullanım desenine göre değişir."
          : "* Estimate only. Actual cost varies by selected model providers and usage patterns."}
      </p>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Pricing FAQ
   ───────────────────────────────────────────── */
const PricingFAQ = () => {
  const t = useT();
  const [openIdx, setOpenIdx] = useState(null);

  const faqs = [
    { q: t("pricing.faq.q1"), a: t("pricing.faq.a1") },
    { q: t("pricing.faq.q2"), a: t("pricing.faq.a2") },
    { q: t("pricing.faq.q3"), a: t("pricing.faq.a3") },
    { q: t("pricing.faq.q4"), a: t("pricing.faq.a4") },
    { q: t("pricing.faq.q5"), a: t("pricing.faq.a5") },
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-3">
      {faqs.map((faq, i) => (
        <div
          key={i}
          className="rounded-xl cursor-pointer transition-colors duration-200"
          style={{
            background: "var(--card-bg)",
            border: openIdx === i ? "1px solid var(--brand-cyan)" : "1px solid var(--border)",
          }}
          onClick={() => setOpenIdx(openIdx === i ? null : i)}
        >
          <div className="flex items-center justify-between px-5 py-4">
            <span className="text-[14px] font-medium" style={{ color: "var(--fg)" }}>
              {faq.q}
            </span>
            <span
              className="text-[18px] transition-transform duration-200 flex-shrink-0 ml-4"
              style={{
                color: "var(--muted)",
                transform: openIdx === i ? "rotate(45deg)" : "rotate(0deg)",
              }}
            >
              +
            </span>
          </div>
          {openIdx === i && (
            <div className="px-5 pb-4 text-[13px] leading-relaxed" style={{ color: "var(--fg)", opacity: 0.8 }}>
              {faq.a}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Pricing Page
   ───────────────────────────────────────────── */
const PricingPage = () => {
  const t = useT();
  const { lang } = useLang();
  const isTr = lang === "tr";
  const [currency, setCurrency] = useState("TRY");
  const [usdTryRate, setUsdTryRate] = useState(DEFAULT_USD_TRY_RATE);

  // Sayfa her yüklendiğinde güncel döviz kurunu çek
  useEffect(() => {
    const fetchRate = async () => {
      try {
        const res = await fetch("https://open.er-api.com/v6/latest/USD");
        const data = await res.json();
        if (data?.rates?.TRY) {
          setUsdTryRate(data.rates.TRY);
        }
      } catch {
        // API hatasında varsayılan kur korunur
      }
    };
    fetchRate();
  }, []);

  const hero = (
    <PageHero
      eyebrow={t("pricing.eyebrow")}
      title={t("pricing.page.heroTitle")}
      subtitle={t("pricing.page.heroSubtitle")}
    />
  );

  return (
    <PageLayout hero={hero}>
      {/* Plan Cards */}
      <PageBanner src="/pricing.png" alt="Pricing banner" height="340px" />

      <SectionReveal>
        <section className="py-10 px-6">
          <PlanCards />
        </section>
      </SectionReveal>

      {/* Currency Toggle */}
      <div className="flex justify-center py-4">
        <div
          className="inline-flex rounded-full p-0.5"
          style={{ background: "var(--border)" }}
        >
          <button
            onClick={() => setCurrency("TRY")}
            className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 ${
              currency === "TRY" ? "text-white" : ""
            }`}
            style={{
              background: currency === "TRY" ? "var(--brand-cyan)" : "transparent",
              color: currency === "TRY" ? "#fff" : "var(--muted)",
            }}
          >
            ₺ TRY
          </button>
          <button
            onClick={() => setCurrency("USD")}
            className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-all duration-200 ${
              currency === "USD" ? "text-white" : ""
            }`}
            style={{
              background: currency === "USD" ? "var(--brand-cyan)" : "transparent",
              color: currency === "USD" ? "#fff" : "var(--muted)",
            }}
          >
            $ USD
          </button>
        </div>
      </div>

      {/* Compare Features */}
      <SectionReveal>
        <section className="py-16 px-6" style={{ background: "var(--section-surface-4)" }}>
          <h2 className="text-center font-bold tracking-tight mb-10" style={{ color: "var(--fg)", fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>
            {t("pricing.page.compareTitle")}
          </h2>
          <CompareTable currency={currency} usdTryRate={usdTryRate} />
        </section>
      </SectionReveal>

      {/* Cost Calculator */}
      <SectionReveal>
        <section className="py-16 px-6">
          <div className="text-center mb-10">
            <h2 className="font-bold tracking-tight mb-3" style={{ color: "var(--fg)", fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>
              {t("pricing.page.calculator.title")}
            </h2>
            <p className="text-[14px]" style={{ color: "var(--muted)" }}>
              {t("pricing.page.calculator.subtitle")}
            </p>
          </div>
          <CostCalculator currency={currency} usdTryRate={usdTryRate} />
        </section>
      </SectionReveal>

      {/* FAQ */}
      <SectionReveal>
        <section className="py-16 px-6" style={{ background: "var(--section-surface-4)" }}>
          <h2 className="text-center font-bold tracking-tight mb-10" style={{ color: "var(--fg)", fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}>
            {t("pricing.faq.title")}
          </h2>
          <PricingFAQ />
        </section>
      </SectionReveal>

      {/* Trusted By */}
      <SectionReveal>
        <div className="py-10 px-6 text-center">
          <p
            className="text-[11px] font-bold uppercase tracking-[0.2em]"
            style={{ color: "var(--muted)", opacity: 0.6 }}
          >
            {t("pricing.trustedBy")}
          </p>
        </div>
      </SectionReveal>
    </PageLayout>
  );
};

export default PricingPage;
